import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Globe, Shield, Building2, TrendingUp, Users, MapPin, ChevronRight, Send, Loader2, Handshake, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PropertyCard from '../components/PropertyCard';
import { getProperties, getVisas, smartSearch, getActiveRegions, getActiveVisas, registerPartner } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';

export default function HomePage() {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [visaPrograms, setVisaPrograms] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeRegions, setActiveRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [partnerFormData, setPartnerFormData] = useState({ name: '', email: '', company: '', phone: '', partner_type: 'developer', country: '', message: '' });
  const [partnerSubmitting, setPartnerSubmitting] = useState(false);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const heroVideoRef = useRef(null);

  // Switch video source based on screen width
  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) return;
    const backendUrl = '';
    const isMobile = window.innerWidth < 768;
    const src = isMobile
      ? `${backendUrl}/api/uploads/videos/hero-banner-mobile.mp4`
      : `${backendUrl}/api/uploads/videos/hero-banner.mp4`;
    video.src = src;
    video.load();
  }, []);

  const handlePartnerRegister = async (e) => {
    e.preventDefault();
    if (!partnerFormData.name || !partnerFormData.email || !partnerFormData.company || !partnerFormData.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    setPartnerSubmitting(true);
    try {
      await registerPartner(partnerFormData);
      toast.success('Registration submitted! Our team will contact you within 48 hours.');
      setShowPartnerForm(false);
      setPartnerFormData({ name: '', email: '', company: '', phone: '', partner_type: 'developer', country: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setPartnerSubmitting(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const result = await smartSearch(searchQuery.trim());
      const filters = result.filters || {};
      const params = new URLSearchParams();
      
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('search', filters.city);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.min_price_usd) params.append('min_price', Math.floor(filters.min_price_usd));
      if (filters.max_price_usd) params.append('max_price', Math.ceil(filters.max_price_usd));
      if (filters.search_text && !filters.city && !filters.country) params.append('search', filters.search_text);
      
      // Fallback: if no filters parsed, use as plain search
      if (params.toString() === '') {
        params.append('search', searchQuery.trim());
      }
      
      navigate(`/properties?${params.toString()}`);
    } catch {
      // Fallback to simple search
      navigate(`/properties?search=${encodeURIComponent(searchQuery.trim())}`);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch independently - don't let one failure block others
        const [propertiesResult, visasResult, regionsResult] = await Promise.allSettled([
          getProperties(),
          getActiveVisas(),
          getActiveRegions()
        ]);
        if (propertiesResult.status === 'fulfilled') setFeaturedProperties(propertiesResult.value.slice(0, 30));
        if (visasResult.status === 'fulfilled') setVisaPrograms(visasResult.value.slice(0, 4));
        if (regionsResult.status === 'fulfilled') setActiveRegions(regionsResult.value.regions || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Fetch partners
    fetch(`/api/partners/list`)
      .then(r => r.json())
      .then(d => setPartners(d.partners || []))
      .catch(() => {});
  }, []);

  const regionImages = {
    'Europe': 'https://images.pexels.com/photos/32621572/pexels-photo-32621572.jpeg',
    'Middle East': 'https://images.pexels.com/photos/818775/pexels-photo-818775.jpeg',
    'Americas': 'https://images.pexels.com/photos/33355364/pexels-photo-33355364.jpeg',
    'Asia': 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg',
    'Africa': 'https://images.pexels.com/photos/3290068/pexels-photo-3290068.jpeg',
    'Oceania': 'https://images.pexels.com/photos/995764/pexels-photo-995764.jpeg',
    'Caribbean': 'https://images.pexels.com/photos/1174732/pexels-photo-1174732.jpeg',
    'South America': 'https://images.pexels.com/photos/2868242/pexels-photo-2868242.jpeg',
  };
  const regions = activeRegions.map(r => ({
    name: r.name,
    count: r.count,
    image: regionImages[r.name] || 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg'
  }));

  const stats = [
    { number: 'Global', label: 'Presence', icon: Globe },
    { number: '100+', label: 'Premium Properties', icon: Building2 },
    { number: '100%', label: 'Visa Success Rate', icon: Shield },
    { number: '500+', label: 'Happy Investors', icon: Users },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            ref={heroVideoRef}
            autoPlay
            muted
            loop
            playsInline
            poster="/api/uploads/videos/hero-poster.jpg"
            className="w-full h-full object-cover"
            data-testid="hero-video"
          >
            <source
              src="/api/uploads/videos/hero-banner.mp4"
              media="(min-width: 768px)"
              type="video/mp4"
            />
            <source
              src="/api/uploads/videos/hero-banner-mobile.mp4"
              type="video/mp4"
            />
          </video>
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="label-overline">India&apos;s Premier Luxury Villa Platform</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mt-6 mb-6 leading-tight tracking-tight">
              Luxury Villas Across <br />
              <span className="text-gradient-gold">India&apos;s Finest Destinations</span>
            </h1>
            <p className="hidden sm:block text-lg sm:text-xl text-white/80 mb-8 max-w-2xl leading-relaxed">
              Discover handpicked villa estates in Alibaug, Goa, Lonavala & Mumbai 3.0. 
              India-first, with a global perspective for discerning investors.
            </p>

            {/* Smart Search - Chat-style */}
            <form onSubmit={handleSearch} className="mb-8 max-w-xl" data-testid="hero-search-form">
              <div className="relative bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden focus-within:border-[#D4AF37]/60 transition-colors">
                <textarea
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSearch(e); } }}
                  placeholder='Try: "4 BHK villa within 5 Cr in Alibaug" or "sea-facing villa in Goa under 10 crore"'
                  className="w-full px-5 py-4 pr-14 bg-transparent text-white placeholder:text-white/40 focus:outline-none resize-none text-base leading-relaxed"
                  rows={2}
                  data-testid="hero-search-input"
                />
                <button
                  type="submit"
                  disabled={searching || !searchQuery.trim()}
                  className="absolute right-3 bottom-3 w-10 h-10 rounded-xl bg-[#D4AF37] hover:bg-[#C5A028] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  data-testid="hero-search-btn"
                >
                  {searching ? (
                    <Loader2 className="h-5 w-5 text-[#0B0C10] animate-spin" />
                  ) : (
                    <Send className="h-5 w-5 text-[#0B0C10]" />
                  )}
                </button>
              </div>
              <p className="text-white/30 text-xs mt-2 ml-1">AI-powered search understands natural language, budgets in INR/USD, and property preferences</p>
            </form>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/properties?country=India">
                <Button className="btn-luxury px-8 py-6 text-base" data-testid="hero-explore-btn">
                  Explore Villas <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/destinations">
                <Button 
                  variant="outline" 
                  className="border-white/30 text-white hover:bg-white/10 hover:text-white px-8 py-6 text-base"
                  data-testid="hero-visa-btn"
                >
                  <Globe className="mr-2 h-5 w-5" /> International Destinations
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-3 bg-[#D4AF37] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1A1D24] border-y border-[#2A2F3A]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <stat.icon className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
                <div className="text-4xl font-semibold text-white heading-display mb-2">
                  {stat.number}
                </div>
                <div className="text-[#A1A1AA] text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* India Luxury Villas - Featured Destinations */}
      <section className="py-24 bg-[#0B0C10]" data-testid="featured-properties">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6">
            <div>
              <span className="label-overline">India Collection</span>
              <h2 className="text-3xl sm:text-4xl text-white mt-4">Luxury Villas Across India</h2>
              <p className="text-[#A1A1AA] mt-3 max-w-xl">Curated villa estates in India&apos;s most sought-after lifestyle destinations — from coastal retreats to hilltop havens.</p>
            </div>
            <Link 
              to="/properties?country=India" 
              className="text-[#D4AF37] hover:text-[#F7E7CE] transition-colors flex items-center mt-4 md:mt-0"
              data-testid="view-all-properties-link"
            >
              View All Indian Properties <ChevronRight className="ml-1 h-5 w-5" />
            </Link>
          </div>

          {/* 4 Destination Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {[
              { name: 'Alibaug', slug: 'alibaug', image: 'https://images.unsplash.com/photo-1757439402277-1a88c7abc89d?w=800', tagline: 'Mumbai\'s Riviera', priceRange: '5 Cr - 15 Cr' },
              { name: 'Mumbai 3.0', slug: 'mumbai-3.0', image: 'https://images.unsplash.com/photo-1622015663319-e97e697503ee?w=800', tagline: 'The New Growth Corridor', priceRange: '3.5 Cr - 10 Cr' },
              { name: 'Lonavala', slug: 'lonavala', image: 'https://images.unsplash.com/photo-1695124568213-f4071fbf516f?w=800', tagline: 'Sahyadri Hillstation Living', priceRange: '3 Cr - 8 Cr' },
              { name: 'Goa', slug: 'goa', image: 'https://images.pexels.com/photos/35808145/pexels-photo-35808145.jpeg?w=800', tagline: 'Coastal Luxury Estates', priceRange: '7 Cr onwards' },
            ].map((dest, index) => (
              <motion.div
                key={dest.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/destinations/${dest.slug}`}
                  className="block group relative h-80 rounded-xl overflow-hidden image-zoom"
                  data-testid={`dest-card-${dest.slug}`}
                >
                  <img src={dest.image} alt={dest.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5">
                    <p className="text-[#D4AF37] text-xs font-medium tracking-widest uppercase mb-1">{dest.tagline}</p>
                    <h3 className="text-xl text-white font-semibold mb-1 group-hover:text-[#D4AF37] transition-colors">{dest.name}</h3>
                    <p className="text-white/60 text-sm">{dest.priceRange}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Featured Indian Properties Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#1A1D24] rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.filter(p => p.country === 'India').slice(0, 6).map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <PropertyCard property={property} />
                </motion.div>
              ))}
              {featuredProperties.filter(p => p.country === 'India').length === 0 && featuredProperties.slice(0, 6).map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <PropertyCard property={property} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* International Presence Section */}
      <section className="py-16 bg-[#1A1D24]" data-testid="regions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <span className="label-overline">Also Active Internationally</span>
              <h2 className="text-2xl sm:text-3xl text-white mt-3 mb-2">Global Investment Destinations</h2>
              <p className="text-[#A1A1AA] max-w-xl text-sm">
                Beyond India, we curate properties with residency-by-investment programs across 
                Dubai, Greece, Singapore, Thailand & London for global diversification.
              </p>
            </div>
            <Link to="/destinations" className="flex-shrink-0">
              <Button className="btn-luxury px-6" data-testid="explore-international-btn">
                <Globe className="mr-2 h-4 w-4" /> Explore International <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
            {[
              { name: 'Dubai, UAE', img: 'https://images.pexels.com/photos/3214995/pexels-photo-3214995.jpeg?w=400', tag: 'Golden Visa' },
              { name: 'Greece', img: 'https://images.unsplash.com/photo-1633909198480-85595aa21285?w=400', tag: 'Golden Visa' },
              { name: 'Singapore', img: 'https://images.pexels.com/photos/777059/pexels-photo-777059.jpeg?w=400', tag: 'GIP' },
              { name: 'London, UK', img: 'https://images.pexels.com/photos/672532/pexels-photo-672532.jpeg?w=400', tag: 'Investor Visa' },
              { name: 'Thailand', img: 'https://images.unsplash.com/photo-1729808641754-381cc885d73f?w=400', tag: 'Residency' },
            ].map((dest) => (
              <Link key={dest.name} to="/destinations" className="group relative h-40 rounded-xl overflow-hidden image-zoom">
                <img src={dest.img} alt={dest.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-[#D4AF37] text-[10px] font-medium tracking-wider uppercase">{dest.tag}</p>
                  <p className="text-white text-sm font-medium group-hover:text-[#D4AF37] transition-colors">{dest.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#1A1D24]" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="label-overline">Why Mulberry World</span>
              <h2 className="text-3xl sm:text-4xl text-white mt-4 mb-6">
                Trusted Partner for <br />
                <span className="text-gradient-gold">Global Investments</span>
              </h2>
              <p className="text-[#A1A1AA] mb-8 leading-relaxed">
                We specialize in helping Indian High Net Worth Individuals navigate the 
                complex world of international real estate and residency programs. Our 
                end-to-end service ensures a seamless journey from property selection 
                to visa approval.
              </p>

              <div className="space-y-6">
                {[
                  { title: 'Expert Guidance', desc: 'Dedicated advisors with deep knowledge of global visa programs' },
                  { title: 'Curated Portfolio', desc: 'Only vetted, premium properties that meet residency requirements' },
                  { title: 'End-to-End Support', desc: 'From property tours to visa documentation and beyond' },
                  { title: 'Local Presence', desc: 'On-ground teams in all 8 countries we operate in' },
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-4"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Link to="/contact" className="inline-block mt-10">
                <Button className="btn-luxury px-8" data-testid="schedule-consultation-btn">
                  Schedule a Consultation <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/29402988/pexels-photo-29402988.jpeg"
                alt="Global mobility"
                className="rounded-xl w-full"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#0B0C10] border border-[#2A2F3A] rounded-xl p-6 max-w-xs">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-white">98%</div>
                    <div className="text-[#A1A1AA] text-sm">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Showcase */}
      {partners.length > 0 && (
        <section className="py-20 bg-[#0B0C10] overflow-hidden" data-testid="partner-showcase-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Handshake className="h-5 w-5 text-[#D4AF37]" />
              <span className="label-overline">Trusted Partners</span>
            </div>
            <h2 className="text-3xl sm:text-4xl text-white mb-4">
              World-Class Partner Network
            </h2>
            <p className="text-[#A1A1AA] max-w-xl mx-auto">
              We partner with the most prestigious developers globally to bring you exceptional investment opportunities.
            </p>
          </div>

          {/* Infinite Scroll Marquee */}
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#0B0C10] to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#0B0C10] to-transparent z-10" />
            <div className="flex animate-marquee gap-6 py-4">
              {[...partners, ...partners].map((p, i) => (
                <Link
                  key={`${p.id}-${i}`}
                  to={`/properties?partner=${encodeURIComponent(p.company || p.name)}`}
                  className="flex-shrink-0 group"
                  data-testid={`partner-card-${p.id}`}
                >
                  <div className="w-64 bg-[#1A1D24] border border-[#2A2F3A] group-hover:border-[#D4AF37]/50 rounded-2xl p-5 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-[#D4AF37]/5 group-hover:-translate-y-1">
                    <div className="flex items-center gap-4 mb-3">
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl object-contain bg-white/5 p-1" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center text-[#D4AF37] font-bold text-lg">
                          {p.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="text-white font-medium text-sm group-hover:text-[#D4AF37] transition-colors">{p.company || p.name}</div>
                        <div className="text-[#A1A1AA] text-xs">{p.country || 'Global'}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#A1A1AA] text-xs">{p.property_count || 0} properties</span>
                      <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0 text-[10px]">{p.status}</Badge>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#0B0C10] via-[#1A1D24] to-[#0B0C10]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="label-overline">Begin Your Journey</span>
          <h2 className="text-3xl sm:text-4xl text-white mt-4 mb-6">
            Ready to Invest in Your Future?
          </h2>
          <p className="text-[#A1A1AA] mb-10 max-w-2xl mx-auto">
            Take the first step towards global residency. Our team of experts is ready to 
            guide you through every step of your international investment journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-luxury px-10 py-6 text-base" data-testid="cta-get-started-btn">
                Get Started Today <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-10 py-6 text-base"
              onClick={() => setShowPartnerForm(true)}
              data-testid="register-partner-btn"
            >
              <Handshake className="mr-2 h-5 w-5" /> Register as Partner
            </Button>
          </div>
        </div>
      </section>

      {/* Partner Registration Modal */}
      <AnimatePresence>
        {showPartnerForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowPartnerForm(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              data-testid="partner-registration-modal"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#2A2F3A]">
                <div>
                  <h3 className="text-lg text-white font-semibold">Partner Registration</h3>
                  <p className="text-[#A1A1AA] text-xs mt-1">Join our network of global property partners</p>
                </div>
                <button onClick={() => setShowPartnerForm(false)} className="text-[#A1A1AA] hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handlePartnerRegister} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm mb-1.5 block">Full Name *</Label>
                    <Input
                      value={partnerFormData.name}
                      onChange={(e) => setPartnerFormData({...partnerFormData, name: e.target.value})}
                      className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                      placeholder="John Smith"
                      required
                      data-testid="partner-reg-name"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm mb-1.5 block">Email *</Label>
                    <Input
                      type="email"
                      value={partnerFormData.email}
                      onChange={(e) => setPartnerFormData({...partnerFormData, email: e.target.value})}
                      className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                      placeholder="partner@company.com"
                      required
                      data-testid="partner-reg-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm mb-1.5 block">Company *</Label>
                    <Input
                      value={partnerFormData.company}
                      onChange={(e) => setPartnerFormData({...partnerFormData, company: e.target.value})}
                      className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                      placeholder="Your Company"
                      required
                      data-testid="partner-reg-company"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm mb-1.5 block">Phone *</Label>
                    <Input
                      value={partnerFormData.phone}
                      onChange={(e) => setPartnerFormData({...partnerFormData, phone: e.target.value})}
                      className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                      placeholder="+91 9999999999"
                      required
                      data-testid="partner-reg-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Partner Type *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {['developer', 'agent'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setPartnerFormData({...partnerFormData, partner_type: type})}
                        className={`p-3 rounded-xl border text-sm text-center transition-all ${
                          partnerFormData.partner_type === type
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-[#2A2F3A] text-[#A1A1AA] hover:border-[#3A3F4A]'
                        }`}
                        data-testid={`partner-reg-type-${type}`}
                      >
                        {type === 'developer' ? (
                          <><Building2 className="h-5 w-5 mx-auto mb-1" /><span>Developer Partner</span></>
                        ) : (
                          <><Users className="h-5 w-5 mx-auto mb-1" /><span>Agent Partner</span></>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Country</Label>
                  <Input
                    value={partnerFormData.country}
                    onChange={(e) => setPartnerFormData({...partnerFormData, country: e.target.value})}
                    className="bg-[#0B0C10] border-[#2A2F3A] text-white"
                    placeholder="Country of operation"
                    data-testid="partner-reg-country"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Message (Optional)</Label>
                  <textarea
                    value={partnerFormData.message}
                    onChange={(e) => setPartnerFormData({...partnerFormData, message: e.target.value})}
                    className="w-full bg-[#0B0C10] border border-[#2A2F3A] text-white rounded-md px-3 py-2 text-sm min-h-[60px] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Tell us about your portfolio..."
                    data-testid="partner-reg-message"
                  />
                </div>
                <Button type="submit" className="w-full btn-luxury h-11" disabled={partnerSubmitting} data-testid="partner-reg-submit">
                  {partnerSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
                <p className="text-[#A1A1AA] text-[10px] text-center">
                  By registering, you agree to our partner terms. <Link to="/partner/plans" className="text-[#D4AF37] underline">View partner plans</Link>
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
